import { useState, useEffect, useContext } from 'react';
import '../table.css';
import { Cog, ExternalLinkAlt } from '@styled-icons/fa-solid';
import { useNavigate } from 'react-router-dom';
import { ProgressBar } from 'react-bootstrap';
import DataTable, { useDataTableHandle } from '../DataTable';
import CopyPastePopUp from '../PopOverCopyPaste/CopyPastePopUp';
import TruncatedTextMagnifierCell from '../TruncatedTextMagnifierCell';
import { StoreContext } from '../Store';
import LoadFiltersV2 from '../LoadFiltersV2/LoadFiltersV2';
import { getDefaultCreatedDates } from '../LoadFiltersV2/utils';
import ResetButton from '../Buttons/ResetButton';
import OverLayWrapper from '../Overlay';
import ConfirmActionModal from '../ConfirmActionModal';
import { useSecurity } from '../Security/security';
import { useMutation } from '@tanstack/react-query';
import { updateStatusViaCog, duplicateLoad } from '../../api/v2/load';
import { useUnifiedLoadList } from './queryHooks/useUnifiedLoadList';
import { useUnifiedDataFileActions } from './queryHooks/useUnifiedDataFile';
import ToastNotification from '../Toast/ToastNotification';
import SaveAsModal from './Modals/SaveAsModal';
import {
  formatUnifiedLoadInvReference,
  showUpdateStatusOption,
  showAmendmentRequiredOption,
  statusMessages,
  confirmModalMessage,
} from '../../utils/unifiedLoads';

const mapUnifiedLoadRow = (row) => ({
  ...row,
  cabieRoute: row.cabieRouteV2 ?? row.cabieRoute,
  invReference: row.invReferenceV2 ?? row.invReference,
  Status: row.statusV2 ?? row.Status,
});

const UnifiedDisplayLoads = () => {
  const [invoices, setInvoices] = useState([]);
  const [requestFailed, setRequestFailed] = useState(false);
  const [paginationLoaded, setPaginationLoaded] = useState(false);
  const [dashboard, setDashboard] = useState(null);

  const [isLoading, setIsLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [filterData, setFilterData] = useState({});
  const [modalConfig, setModalConfig] = useState({ show: false, type: '', load: null });

  const navigate = useNavigate();
  const dth = useDataTableHandle();

  const store = useContext(StoreContext);
  const persistedFilters = store.cabieFilters[0].filters || {};
  const hasFiltersApplied = !!Object.keys(persistedFilters).length || dth?.current?.sortStatus?.currentKey;
  const { defaultCreatedFrom, defaultCreatedTo } = getDefaultCreatedDates();

  const { validateRole } = useSecurity();
  const isTraderUser = validateRole({ department: 'TRADER' });
  const isItUser = validateRole({ department: 'IT' });
  const isCmtUser = validateRole({ department: 'CMT' });

  const [openSaveAsModal, setOpenSaveAsModal] = useState(false);
  const [saveLoadAs, setSaveLoadAs] = useState(null);
  const [selectedLoadId, setSelectedLoadId] = useState(null);
  const [toastConfig, setToastConfig] = useState({ show: false, type: 'success', message: '' });

  const statusOptions = ['Draft', 'Submitted', 'In progress', 'Completed'].map((s) => {
    return { data: s, id: s };
  });

  const updateStatusMutation = useMutation({
    mutationFn: async ({ loadId, status }) => await updateStatusViaCog(loadId, status),
    onSuccess: (_data, { status }) => {
      setToastConfig({
        show: true,
        type: 'success',
        message: statusMessages(status).success,
      });
      fetchData();
      setSaveLoadAs('');
    },
  });

  const duplicateLoadMutation = useMutation({
    mutationFn: async ({ loadId }) => await duplicateLoad(loadId),
    onSuccess: () => {
      fetchData();
    },
  });

  const { unifiedLazyLoadMutation } = useUnifiedLoadList();
  const { downloadUnifiedDataFileMutation } = useUnifiedDataFileActions();

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    setDashboard(urlParams.get('dashboard') !== null ? urlParams.get('dashboard') : '');
    store.cabiePersist[1]({});

    if (hasFiltersApplied) {
      setFilterData({ ...persistedFilters });
      setShowFilters(true);
    }
  }, []);

  useEffect(() => {
    if (paginationLoaded && dashboard !== null) fetchData();
  }, [paginationLoaded, dashboard]);

  const fetchData = async (newFilters, goToFirstPage = false, resetSort = false) => {
    await fetchLoads(null, newFilters, goToFirstPage, resetSort);
  };

  const updateData = async (setPaginationLength, pagination, sortStatus, search) => {
    await fetchLoads({ setPaginationLength, pagination, sortStatus, search }, null, null);
  };

  const fetchLoads = async (dataTableProps, filters, goToFirstPage, resetSort = false) => {
    try {
      const pagination = dataTableProps?.pagination || dth.current.pagination;
      const sortStatus = resetSort ? {} : dataTableProps?.sortStatus || dth.current.sortStatus;
      const paginationSetter = dataTableProps?.setPaginationLength || dth.current.setServerDataLength;

      const filtersToSend = filters || persistedFilters;

      const payload = {
        skip: goToFirstPage ? 0 : pagination.pageNo * pagination.itemsPerPage,
        amount: pagination.itemsPerPage,
        sort_by: sortStatus.currentKey,
        sort_order: sortStatus.direction,
        dashboardElement: dashboard,
        search: dataTableProps?.search,
        filter: {
          excludeCompleted: 1,
          createdDateFrom: defaultCreatedFrom,
          createdDateTo: defaultCreatedTo,
          ...filtersToSend,
        },
        applyFilters: Object.keys(filters || persistedFilters).length ? 1 : 0,
      };

      setIsLoading(true);
      const data = await unifiedLazyLoadMutation.mutateAsync(payload);
      paginationSetter(data?.totalItems ?? 0);
      if (goToFirstPage) {
        dth.current.resetPagination();
      }
      const rows = Array.isArray(data?.data) ? data.data : [];
      setInvoices(rows.map(mapUnifiedLoadRow));
      setIsLoading(false);
    } catch (err) {
      setRequestFailed(true);
      setIsLoading(false);
    }
  };

  const handleReset = async () => {
    dth.current.resetPagination();
    dth.current.setSortStatus({});
    store.cabieFilters[1]({});
    setFilterData({});
    await fetchData({}, true, true);
    setDashboard('');
  };

  const handleSubmit = async () => {
    store.cabieFilters[1]({ filters: { ...filterData } });
    await fetchData({ ...filterData }, true);
  };

  const handleCogClick = (action, load) => {
    if (action === 'update') {
      setOpenSaveAsModal(true);
      setSelectedLoadId(parseInt(load.id));
    } else {
      setModalConfig({ show: true, type: action, load: load });
    }
  };

  const handleModalClose = () => {
    setModalConfig({ show: false, type: '', load: null });
  };

  const handleModalConfirm = async () => {
    const { type, load } = modalConfig;
    if (type === 'duplicate') {
      try {
        await duplicateLoadMutation.mutateAsync({ loadId: load.id });
      } catch (error) {
        console.error('Error duplicating load:', error);
        setToastConfig({ show: true, type: 'error', message: 'Failed to duplicate load.' });
      }
    }

    if (type === 'cancel') {
      await onUpdateStatus(load.id, 'Cancelled');
    }

    if (type === 'restore') {
      await onUpdateStatus(load.id, 'Restored');
    }

    if (type === 'amendment') {
      await onUpdateStatus(load.id, 'Amendment Required');
    }

    if (type === 'download') {
      try {
        const portalRef = load.portalRef ?? load['Ref No.'];
        await downloadUnifiedDataFileMutation.mutateAsync({ loadId: load.id, fileName: `CABIE-${portalRef}` });
      } catch (error) {
        console.error('Error downloading unified load file:', error);
        setToastConfig({ show: true, type: 'error', message: 'Failed to download data file.' });
      }
    }

    handleModalClose();
  };

  const onUpdateStatus = async (loadId, status) => {
    try {
      await updateStatusMutation.mutateAsync({ loadId, status });
    } catch (error) {
      console.error('Error updating load status:', error);
      setToastConfig({ show: true, type: 'error', message: statusMessages(status).error });
      setSaveLoadAs(null);
    }
  };

  if (requestFailed) return <p>Failed!</p>;

  return (
    <>
      <ToastNotification
        show={toastConfig.show}
        onClose={() => setToastConfig({ ...toastConfig, show: false })}
        type={toastConfig.type}
        message={toastConfig.message}
      />

      <div className="full-width-container">
        <section className="floating-box" style={{ maxWidth: '1500px' }}>
          <div className="d-flex justify-content-between px-3 pt-4">
            <h1 className="page-heading">CABIE Loads</h1>
            <div className="d-flex gap-4 align-items-center">
              {hasFiltersApplied && <ResetButton onClick={handleReset} inline />}
              <button
                type="button"
                className="stepper-button"
                onClick={() => setShowFilters(!showFilters)}
                style={{ minWidth: 145, maxWidth: 145 }}
                disabled={hasFiltersApplied && showFilters}
              >
                {showFilters ? 'Hide' : 'Show'} Filters
              </button>
            </div>
          </div>
          <LoadFiltersV2
            visible={showFilters}
            values={filterData}
            defaultValues={{ defaultCreatedFrom, defaultCreatedTo }}
            onChange={setFilterData}
            onSubmit={handleSubmit}
          />
          <div>
            <div className="px-3 pt-4">
              <DataTable
                data={invoices}
                handle={dth}
                className="table-striped"
                lazyLoading
                advancedSearch
                sortable
                onUpdate={updateData}
                onPaginationLoaded={() => {
                  setPaginationLoaded(true);
                }}
                handleReset={handleReset}
                persistGetter={store.cabiePersist[0]}
                persistSetter={store.cabiePersist[1]}
                isLoading={isLoading}
              >
                <DataTable.Headers
                  style={{
                    fontSize: '14.4px',
                    backgroundColor: '#fff',
                    paddingBottom: '10px',
                    borderBottom: '1px solid #ddd',
                    borderTop: '1px solid #ddd',
                  }}
                />
                <DataTable.Row style={{ fontSize: '15px' }} />
                <DataTable.Pagination itemsPerPage={[10, 25, 50]} />
                <DataTable.Column colKey="cabieRoute" header="Route" />
                <DataTable.Column colKey="Ref No." header="Ref No." searchOpts={{ type: 'text', sendAs: 'refno' }} />
                <DataTable.Column
                  colKey="customerName"
                  header="Customer Name"
                  maxWidth="20ch"
                  searchOpts={{ type: 'text', sendAs: 'customerName' }}
                >
                  {({ customerName }) => {
                    return (
                      <div className="d-flex align-items-center justify-content-between">
                        {customerName?.length > 28 ? `${customerName?.slice(0, 28)}` : customerName}
                        {customerName?.length > 28 ? <CopyPastePopUp jobRef={customerName} /> : null}
                      </div>
                    );
                  }}
                </DataTable.Column>
                <DataTable.Column colKey="eoriNumber" header="EORI" maxWidth="15ch" />
                <DataTable.Column
                  colKey="Name"
                  header="Name"
                  maxWidth="15ch"
                  searchOpts={{ type: 'text', sendAs: 'name' }}
                />

                <DataTable.Column
                  colKey="Loading date"
                  header="Loading Date"
                  maxWidth="18ch"
                  searchOpts={{ type: 'date', sendAs: 'loadDate' }}
                  sort={(a, b) => {
                    if (!a) return 1;
                    if (!b) return -1;
                    const corrected = (d) => `20${d.slice(-2)}-${d.slice(3, 5)}-${d.slice(0, 2)}`;
                    const aDate = new Date(corrected(a));
                    const bDate = new Date(corrected(b));
                    if (aDate > bDate) return 1;
                    if (aDate < bDate) return -1;
                    return 0;
                  }}
                />
                <DataTable.Column
                  colKey="Jira Ticket"
                  header="JIRA Ticket"
                  maxWidth="16ch"
                  searchOpts={{ type: 'text', sendAs: 'jiraTicket' }}
                >
                  {({ 'Jira Ticket': jiraTicket }) => {
                    return (
                      <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {jiraTicket}
                      </div>
                    );
                  }}
                </DataTable.Column>
                <DataTable.Column
                  colKey="Job reference"
                  header="Job Ref"
                  maxWidth="12ch"
                  style={{ minWidth: 0 }}
                  searchOpts={{ type: 'text', sendAs: 'jobReference' }}
                >
                  {({ 'Job reference': jobReference }) => <TruncatedTextMagnifierCell text={jobReference} />}
                </DataTable.Column>
                <DataTable.Column
                  colKey="invReference"
                  header="Inv Ref"
                  maxWidth="12ch"
                  style={{ minWidth: 0 }}
                  searchOpts={{ type: 'text', sendAs: 'invReference' }}
                >
                  {({ invReference }) => (
                    <TruncatedTextMagnifierCell text={formatUnifiedLoadInvReference(invReference)} />
                  )}
                </DataTable.Column>
                <DataTable.Column
                  colKey="Status"
                  header="Status"
                  searchOpts={{ type: 'select', src: statusOptions, sendAs: 'status' }}
                >
                  {({ Status }) => {
                    return (
                      <div>
                        {Status === 'Waiting for Customer' && isTraderUser ? (
                          <div>
                            {Status}
                            <span className="warning" style={{ marginLeft: '9px', height: '15px' }}></span>
                          </div>
                        ) : (
                          Status
                        )}
                      </div>
                    );
                  }}
                </DataTable.Column>
                <DataTable.Column colKey="Progress" header="Progress">
                  {({ Progress, Status }) => {
                    if (Status === 'Cancelled') {
                      return <ProgressBar now={100} label="" style={{ width: '6rem' }} variant="secondary" />;
                    }
                    if (!Progress) {
                      return <ProgressBar now={100} label="0%" style={{ width: '6rem' }} variant="danger" />;
                    }
                    let progressVariant;
                    if (Progress <= 50) {
                      progressVariant = 'warning';
                    } else if (Progress < 100) {
                      progressVariant = 'info';
                    } else if (Progress >= 100) {
                      progressVariant = 'success';
                    }

                    return (
                      <ProgressBar
                        now={Progress}
                        label={`${Progress}%`}
                        style={{ width: '6rem' }}
                        variant={progressVariant}
                      />
                    );
                  }}
                </DataTable.Column>
                <DataTable.Column
                  className="p-0"
                  style={{ height: '1px' }}
                  header="Actions"
                  headerStyle={{ textAlign: 'center' }}
                >
                  {(load) => (
                    <OverLayWrapper
                      placement="top"
                      trigger={['click']}
                      rootClose={true}
                      popoverContent={
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                          <h6 className="list-loads-cog-icon-title">{load?.['Ref No.']}</h6>
                          {load?.Status !== 'Cancelled' && (
                            <button
                              type="button"
                              className="list-loads-cog-icon-button"
                              style={{ textAlign: 'left', padding: '0.25rem 0.5rem' }}
                              onClick={() => handleCogClick('cancel', load)}
                            >
                              Cancel
                            </button>
                          )}
                          {load?.Status === 'Cancelled' && (isCmtUser || isItUser) && (
                            <button
                              type="button"
                              className="list-loads-cog-icon-button"
                              style={{ textAlign: 'left', padding: '0.25rem 0.5rem' }}
                              onClick={() => handleCogClick('restore', load)}
                            >
                              Restore
                            </button>
                          )}
                          <button
                            type="button"
                            className="list-loads-cog-icon-button"
                            style={{ textAlign: 'left', padding: '0.25rem 0.5rem' }}
                            onClick={() => handleCogClick('duplicate', load)}
                          >
                            Duplicate
                          </button>
                          {load?.Status !== 'CM8 Draft' && (
                            <button
                              type="button"
                              className="list-loads-cog-icon-button"
                              style={{ textAlign: 'left', padding: '0.25rem 0.5rem' }}
                              onClick={() => handleCogClick('download', load)}
                            >
                              CABIE Data File
                            </button>
                          )}

                          {showUpdateStatusOption(load.Status) && (
                            <button
                              type="button"
                              className="list-loads-cog-icon-button"
                              style={{ textAlign: 'left', padding: '0.25rem 0.5rem' }}
                              onClick={() => handleCogClick('update', load)}
                            >
                              Update Status
                            </button>
                          )}

                          {showAmendmentRequiredOption(load.Status, load.sfdHasMrn) && (
                            <button
                              type="button"
                              className="list-loads-cog-icon-button"
                              style={{ textAlign: 'left', padding: '0.25rem 0.5rem' }}
                              onClick={() => handleCogClick('amendment', load)}
                            >
                              Amendment Required
                            </button>
                          )}
                        </div>
                      }
                      popoverStyle={{ minWidth: '8rem', padding: '0.5rem' }}
                    >
                      <button type="button" className="external-table-link">
                        <Cog width="1rem" height="1rem" />
                      </button>
                    </OverLayWrapper>
                  )}
                </DataTable.Column>
                {!isItUser && (
                  <DataTable.Column
                    className="p-0"
                    style={{ height: '1px' }}
                    header="View"
                    headerStyle={{ textAlign: 'center' }}
                  >
                    {({ id, customerName, ...rest }) => (
                      <div className="d-flex justify-content-center align-items-center w-100 h-100">
                        <button
                          type="button"
                          className="external-table-link"
                          onClick={() => {
                            store.clientIDname[1](customerName);
                            navigate({ pathname: `/load/${id}` });
                          }}
                          name={`${rest['Ref No.']}-unify`}
                        >
                          <ExternalLinkAlt width="1rem" height="1rem" />
                        </button>
                      </div>
                    )}
                  </DataTable.Column>
                )}
              </DataTable>
            </div>
          </div>
        </section>

        <ConfirmActionModal
          show={modalConfig.show}
          title={modalConfig.load?.['Ref No.'] || ''}
          message={confirmModalMessage(modalConfig.type)}
          onClose={handleModalClose}
          onConfirm={handleModalConfirm}
        />
        <SaveAsModal
          show={openSaveAsModal}
          onHide={() => setOpenSaveAsModal(false)}
          onSave={(loadId, status) => onUpdateStatus(loadId, status)}
          loadId={selectedLoadId}
          saveLoadAs={saveLoadAs}
          setSaveLoadAs={setSaveLoadAs}
        />
      </div>
    </>
  );
};

export default UnifiedDisplayLoads;
